import {
  HEARTPLACE_COBALT_APPOINTMENT_TYPE_TO_VISIT_TYPE_MAP,
  HEARTPLACE_COBALT_STATUS_CODE_MAP,
  HEARTPLACE_COBALT_VISIT_TYPE_MAP,
} from "@/configuration/groups/ushv-heartplace/configuration";
import ushvPimaInsuranceMappingData from "@/configuration/groups/ushv-pima/insurance-mapping.json";
import { AppointmentStatus } from "@/packages/shared/src/models/appointment";
import { DocRoutingStrategy } from "@/src/lib/constants";
import {
  buildPatientMetadataInsuranceVariables,
  resolveCoverageEcwId,
  type EcwInsuranceMapping,
  type EcwInsuranceMappingEntry,
  type PatientMetadataInsuranceVariables,
} from "@/src/lib/ecw-insurance-mapping";
import type { SpecialCaseConfig } from "@/src/lib/special-cases/types";
import { HolidayNames } from "@/src/lib/utils/holidays";
import { AppointmentType } from "@/src/models/appointment";
import { Classification, ClassificationCategory, ClassificationSection } from "@/src/models/constants/classifications";
import { Workflow, type WorkflowType } from "@/src/models/constants/group";
import { Coverage } from "@/src/models/coverage";
import { Referral } from "@/src/models/documents/referral";
import { Section } from "@/src/models/section";

export const USHV_PIMA_PAGE_RANGES_RULE =
  "patientIndex identifies the PATIENT, never the document: assign a new patientIndex ONLY when a DIFFERENT PATIENT (different name) appears in the fax. " +
  "Every section belonging to the same patient shares that patient's single patientIndex, no matter how many documents, encounters, or dates the fax contains. " +
  "A different date of service, visit date, encounter, ordering provider, or new document header is NEVER a reason for a new patientIndex or a separate pageRange — multiple dated progress notes, charts, or reports for the same patient all keep one patientIndex. " +
  "When comparing patient names, ignore capitalization differences (e.g., 'JOHN DOE' and 'John Doe' are the same patient). " +
  "Treat names with minor typos or small spelling variations as the same patient (e.g., 'Jon Smith' and 'John Smith' should be grouped together). " +
  "If a page has no patient name, group it with adjacent pages that DO have a name based on context clues like formatting, date proximity, or document structure.";

// Pima shares Heartplace's eCW/Cobalt instance (same practice code, DJDGDD) and Cobalt access
// token (see COBALT_ACCESS_TOKEN_ENV_BY_GROUP_ID in src/lib/web-automation/cobalt/env.ts), so it
// reuses Heartplace's eCW status/visit-type code conventions rather than defining its own.
export const PIMA_COBALT_STATUS_CODE_MAP: Record<string, AppointmentStatus> = HEARTPLACE_COBALT_STATUS_CODE_MAP;

export const PIMA_COBALT_VISIT_TYPE_MAP: Record<string, AppointmentType> = {
  ...HEARTPLACE_COBALT_VISIT_TYPE_MAP,
  // Heartplace's map has no "migrated" entry, so it falls through to the INITIAL_CONSULTATION
  // default in mapCobaltVisitTypeToAppointmentType — added here so migrated encounters are
  // correctly excluded per Pima's "not type TEL, MIGRATED, or WEB" eligibility rule.
  migrated: AppointmentType.UNKNOWN,
};

export const PIMA_COBALT_APPOINTMENT_TYPE_TO_VISIT_TYPE_MAP: Partial<Record<AppointmentType, string>> =
  HEARTPLACE_COBALT_APPOINTMENT_TYPE_TO_VISIT_TYPE_MAP;

// Faith's explicit list for Pima's classifications (replaces the Heartland-copied baseline used
// during onboarding — see docs discussion on PR #7490 for that history).
const USHV_PIMA_CLASSIFICATION_MAP = {
  [ClassificationCategory.CLASSIFICATION]: [
    Classification.LAB_RESULTS,
    Classification.LAB_RESULTS_BP,
    Classification.LAB_RESULTS_PT_INR,
    Classification.MEDICAL_HISTORY_CHART_FROM_CLINIC,
    Classification.MEDICAL_HISTORY_CHART_FROM_HOSPITAL,
    Classification.MEDICAL_RECORD_REQUEST,
    Classification.CLEARANCE_REQUEST,
    Classification.PRIOR_AUTHORIZATION,
    Classification.PROCEDURE_CARDIAC,
    Classification.PROCEDURE_DEVICE_INFO,
    Classification.PROCEDURE_REHAB,
    Classification.PROCEDURE_SLEEP,
    Classification.PROGRESS_NOTE_OFFICE_VISIT,
    Classification.PROGRESS_NOTE_ER_REPORT,
    Classification.REFERRAL,
  ],
  [ClassificationCategory.OTHER]: [Classification.OTHER],
  [ClassificationCategory.IMAGING_RESULTS]: [
    Classification.IMAGING_RESULTS_CT,
    Classification.IMAGING_RESULTS_ECHO,
    Classification.IMAGING_RESULTS_EKG,
    Classification.IMAGING_RESULTS_HOLTER_MONITOR,
    Classification.IMAGING_RESULTS_MAMMOGRAM,
    Classification.IMAGING_RESULTS_MRI,
    Classification.IMAGING_RESULTS_NM,
    Classification.IMAGING_RESULTS_OTHER,
    Classification.IMAGING_RESULTS_STRESS_TEST_EXERCISE,
    Classification.IMAGING_RESULTS_ULTRASOUND,
    Classification.IMAGING_RESULTS_X_RAY,
  ],
};

export function getUshvPimaClassificationsToDisplayForSelection(): ClassificationSection[] {
  return Object.entries(USHV_PIMA_CLASSIFICATION_MAP).map(([label, options]) => ({
    label,
    options: options.map((option) => option.toLowerCase()),
  }));
}

const USHV_PIMA_HOLIDAYS = [
  HolidayNames.NEW_YEARS_DAY,
  HolidayNames.MARTIN_LUTHER_KING_JR_DAY,
  HolidayNames.MEMORIAL_DAY,
  HolidayNames.INDEPENDENCE_DAY,
  HolidayNames.THANKSGIVING,
  HolidayNames.BLACK_FRIDAY,
  HolidayNames.CHRISTMAS_EVE,
  HolidayNames.CHRISTMAS_DAY,
] as const;

export function getUshvPimaHolidays(): HolidayNames[] {
  return [...USHV_PIMA_HOLIDAYS];
}

export function getUshvPimaIntakeClassification(classification: Classification): Classification {
  switch (classification) {
    case Classification.COVER_PAGE:
    case Classification.REFERRAL:
    case Classification.RESPONSE_TO_VH_REQUEST:
    case Classification.IDENTIFICATION_CARD:
    case Classification.INSURANCE_CARD:
    case Classification.INTAKE:
      return Classification.REFERRAL;
    default:
      return classification;
  }
}

const USHV_PIMA_DISPOSITION_REVIEW_CLASSIFICATIONS = new Set<Classification>([
  Classification.PROGRESS_NOTE_OFFICE_VISIT,
  Classification.PROGRESS_NOTE_ER_REPORT,
  Classification.MEDICAL_HISTORY_CHART_FROM_CLINIC,
  Classification.MEDICAL_HISTORY_CHART_FROM_HOSPITAL,
]);

// All PIMA-supported classifications, including OTHER, are extract-eligible so that every section
// in a mixed document gets its own extraction run and appears as a separate section in the intake
// UI — mirroring Heartland (USHV) behavior. Excluding OTHER here previously caused it to disappear
// silently instead of landing on a REVIEW_NEEDED doc-management task; don't re-exclude it.
export function getUshvPimaClassificationToExtract(): Classification[] {
  return Object.values(USHV_PIMA_CLASSIFICATION_MAP).flat();
}

export function shouldUshvPimaForceIntakeReview(classifications: Classification[]): boolean {
  return classifications.some((classification) => USHV_PIMA_DISPOSITION_REVIEW_CLASSIFICATIONS.has(classification));
}

// Intake creation for PIMA is gated on a referral (or an OTHER catch-all page) being present.
// Documents that contain only non-referral, non-OTHER sections (e.g. standalone imaging results)
// should not proceed through Intake — they fall through to disposition instead.
export function getUshvPimaIntakeGatingClassifications(): Classification[] {
  return [Classification.REFERRAL, Classification.OTHER];
}

export function getUshvPimaClassificationForDocumentUpload(): Classification[] {
  return Object.values(USHV_PIMA_CLASSIFICATION_MAP).flat();
}

export function getUshvPimaWorkflows(): Partial<Record<WorkflowType, Classification[]>> {
  return {
    [Workflow.REFERRAL]: [
      Classification.INTAKE,
      Classification.REFERRAL,
      Classification.REFERER_INFORMATION,
      Classification.RESPONSE_TO_VH_REQUEST,
    ],
    [Workflow.DOC_MANAGEMENT]: getUshvPimaClassificationForDocumentUpload().filter(
      (c) => c !== Classification.REFERRAL
    ),
  };
}

export function getUshvPimaImagingFilenamePrefix(): string {
  return "";
}

const USHV_PIMA_WESTFAX_PRODUCT_ID = "ad98ea13-8a03-47fc-955d-c2cc8ac824e0";

export const USHV_PIMA_WESTFAX_PRODUCT_IDS = [USHV_PIMA_WESTFAX_PRODUCT_ID] as const;

const USHV_PIMA_DISPOSITION_EMAIL = "CSOIndexers@pimaheartandvascular.com";

export function getUshvPimaDispositionEmailRecipients(): string[] {
  return [USHV_PIMA_DISPOSITION_EMAIL];
}

export type UshvPimaInsuranceMappingEntry = EcwInsuranceMappingEntry;

export type UshvPimaInsuranceMapping = EcwInsuranceMapping;

export const USHV_PIMA_INSURANCE_MAPPING: UshvPimaInsuranceMapping =
  ushvPimaInsuranceMappingData as UshvPimaInsuranceMapping;

export function resolveUshvPimaCoverageEcwId(coverage: Coverage | null, forPrimary: boolean): string | null {
  return resolveCoverageEcwId(coverage, USHV_PIMA_INSURANCE_MAPPING, forPrimary);
}

export function getUshvPimaPatientMetadataInsuranceVariables(section: Section): PatientMetadataInsuranceVariables {
  const referral = section.reviewedContent as Referral | null;
  const primary = referral?.primaryCoverage ?? null;
  const secondary = referral?.secondaryCoverage ?? null;
  const primaryEcwId = resolveUshvPimaCoverageEcwId(primary, true);
  const secondaryEcwId = resolveUshvPimaCoverageEcwId(secondary, false);

  return buildPatientMetadataInsuranceVariables(primary, secondary, primaryEcwId, secondaryEcwId);
}

const USHV_PIMA_ECW_PLAN_IDS_REQUIRING_CONSULT: ReadonlySet<string> = new Set([
  "1040", // 87726 UnitedHealthcare — UHC Dual AHCCCS Medicare/Medicaid
  "40", // 03432 United HealthCare Community Plan of Arizona — UHC Community AHCCCS Medicaid
  "21", // 60054 Aetna — Aetna
  "28", // 60054 Aetna — Aetna POS
  "932", // 60054 Aetna — Aetna Banner Health
  "864", // 60054 Aetna — Aetna HCA Healthcare
  "30", // 60054 Aetna — Aetna SRC
  "38", // 86052 Mercy Care Plan — AHCCCS Mercy Care Plan
  "37", // 86052 Mercy Care Plan — AHCCCS Mercy Care Advantage
  "354", // 61101 Humana — Humana Gold Choice HMO
  "355", // 61101 Humana — Humana Gold Choice PPO
  "36", // RP105 Health Choice BCBS of Arizona ACA — AHCCCS Health Choice of AZ
  "34", // 66901 Banner University Medicaid — AHCCCS Banner University Family Care
  "35", // 66901 Banner University Medicaid — AHCCCS Banner University Family Care LTC
  "33", // 68069 Ambetter — AHCCCS AZCH Complete Care Plan
  "90", // 68069 Ambetter — AZCH Complete Care Plan Allwell
  "91", // 68069 Ambetter — AZCH Complete Care Plan Ambetter
  "329", // 99726 TRICARE West Region — Healthnet Federal Services Tricare Select
  "638", // TWVACCN TriWest Healthcare Alliance — TriWest VA CCN
  "452", // MTAIN Meritain Health
  "270", // 06603 ASA GEHA
  "961", // IEXAZ Imperial Insurance Companies Inc - Arizona Exchange
  "942", // 00290 Indian Health Services — Yoeme Health Plan
  "894", // 25141 Group Health Plan (GHP) — Group Health COOP Multiplan
  "694", // 75261 Web TPA — WebTPA
]);

export function ushvPimaEcwPlanNeedsConsult(ecwId: string | null): boolean {
  return ecwId != null && USHV_PIMA_ECW_PLAN_IDS_REQUIRING_CONSULT.has(ecwId);
}

// Special-case insurance lists. Source: "PIMA Insurance Mapping - Mapping for Ops" (ops-provided
// PDF, 2026-08-19), column I ("Do Not Schedule") — this is the sole source of truth for the
// blacklist; do not merge in payers from the prior spreadsheet-derived lists.
//
// Blacklisting is unconditional — a payer belongs here only if ops never schedules it. Column I
// also carried conditional entries (UHC when the member id began with a letter, Aetna/Banner when
// the plan name contained "CVS"); those were retired because the conditions blocked referrals ops
// does schedule. A payer that should block only sometimes needs a new mechanism, not this list.
const USHV_PIMA_INSURANCE_BLACKLIST: string[] = [
  "Allied Benefit Systems Incorporated",
  "Oscar Health",
  "Imperial Insurance Companies Inc - Arizona Exchange",
  "MotivHealth",
  "Antidote Health Plan",
  "Medica Health Plan Solutions",
  "P3 Health Partners-Arizona",
];

// "AHCCCS" matches any AHCCCS plan — spreadsheet specifies commercial only.
export const USHV_PIMA_INSURANCE_CONSULT_NEEDED: string[] = [
  "Aetna Commercial",
  "AHCCCS",
  "Allwell",
  "Ambetter",
  "AZCH",
  "Centurion",
  "GEHA",
  "Group Health Cooperative",
  "Healthnet",
  "Tricare",
  "Humana Commercial",
  "Imperial Insurance",
  "Meritain",
  "Triwest",
  "VACAA",
  "WEB TPA",
  "Yoeme",
];

export const USHV_PIMA_SYNCOPE_ICD10_PREFIXES: readonly string[] = ["R55"];
export const USHV_PIMA_PREPROCEDURAL_EXAM_ICD10_PREFIXES: readonly string[] = ["Z0181"];

export function getUshvPimaSpecialCaseConfig(): SpecialCaseConfig {
  return {
    insuranceBlacklist: USHV_PIMA_INSURANCE_BLACKLIST,
    insuranceConsultNeeded: [],
    excludeDiagnosisTextFromReasonChecks: true,
    specialDiagnoses: [
      {
        label: "Cardiac Clearance",
        icdPrefixes: [...USHV_PIMA_PREPROCEDURAL_EXAM_ICD10_PREFIXES],
      },
      {
        label: "Chest Pain",
        icdPrefixes: ["R07", "I20"],
        keywords: ["chest pain", "chest tightness"],
      },
      {
        label: "Angina Pectoris",
        icdPrefixes: ["I20"],
        keywords: ["angina"],
      },
      {
        label: "Shortness of Breath",
        icdPrefixes: ["R06"],
        keywords: ["shortness of breath", "dyspnea"],
      },
    ],
  };
}

export function getUshvPimaScheduleConfigPath(): string {
  return "configuration/groups/ushv-pima/rules/schedule.yaml";
}

export function getUshvPimaDocRoutingStrategy(section: Section): DocRoutingStrategy[] {
  const classification = section.uploadClassification || section.classification;

  return classification === Classification.OTHER ? [] : [DocRoutingStrategy.EHR];
}
